/** Shared measurement contract for history, rankings and awards. Durations are
 * whole seconds; a finished overall session counts once, never once per focus. */
export type PracticePeriod = "day" | "week" | "month" | "year" | "all";
export type PracticeTool = "groove" | "metronome" | "other";
export const PRACTICE_METRICS_VERSION = 1;
export const PRACTICE_METRICS_MIN_DAY = "1970-01-01";
export const PRACTICE_METRICS_MAX_PAGE = 10_000;
/** Bounds for a learner's stored weekly practice goal, mirrored by the table CHECK. */
export const PRACTICE_WEEKLY_GOAL_MIN_MINUTES = 15;
export const PRACTICE_WEEKLY_GOAL_MAX_MINUTES = 600;
export const STREAK_SESSION_SECONDS = 600;
export const PRACTICE_INSTRUMENTS = ["piano", "guitar", "bass", "drums", "singing", "violin", "viola", "cello", "flute", "clarinet", "saxophone", "trumpet", "ukulele"] as const;
/** Match the API's strict YYYY-MM-DD parser and its supported lower bound. */
export function isValidPracticeDay(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value < PRACTICE_METRICS_MIN_DAY) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}
/** Invalid and future saved dates return to today's safe browse default. */
export function normalizePracticeDay(value: string | null | undefined, today: string): string {
  return value && isValidPracticeDay(value) && value <= today ? value : today;
}
/** Parse a URL page with the same inclusive bounds as the Hub API. */
export function practiceBrowsePage(value: string | null | undefined): number {
  if (!value || !/^\d+$/.test(value)) return 1;
  const page = Number(value);
  return Number.isSafeInteger(page) && page >= 1 && page <= PRACTICE_METRICS_MAX_PAGE ? page : 1;
}
export function normalisePracticeInstrument(value: string | null | undefined): string | null {
  const text = value?.trim().toLowerCase();
  if (!text) return null;
  if (/piano|keyboard/.test(text)) return "piano";
  if (/bass/.test(text)) return "bass";
  if (/guitar/.test(text)) return "guitar";
  if (/drum|percussion/.test(text)) return "drums";
  if (/sing|vocal|voice/.test(text)) return "singing";
  return PRACTICE_INSTRUMENTS.find(instrument => text.includes(instrument)) ?? text.slice(0, 80);
}
export function practiceDay(value: Date | string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(value));
  const get = (key: string) => parts.find(part => part.type === key)!.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}
export function shiftPracticeDay(day: string, days: number): string {
  return new Date(Date.parse(`${day}T12:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}
export function practiceWeek(day: string): string {
  return shiftPracticeDay(day, -((new Date(`${day}T12:00:00Z`).getUTCDay() + 6) % 7));
}
export function practiceRange(period: PracticePeriod, on: string): { start: string; end: string; previousStart: string; previousEnd: string } {
  const d = new Date(`${on}T12:00:00Z`), year = d.getUTCFullYear(), month = d.getUTCMonth();
  const iso = (y: number, m: number, day: number) => new Date(Date.UTC(y, m, day, 12)).toISOString().slice(0, 10);
  if (period === "day") return { start: on, end: on, previousStart: shiftPracticeDay(on,-1), previousEnd: shiftPracticeDay(on,-1) };
  if (period === "week") { const start = practiceWeek(on); return { start, end: shiftPracticeDay(start,6), previousStart:shiftPracticeDay(start,-7),previousEnd:shiftPracticeDay(start,-1) }; }
  if (period === "month") return {start:iso(year,month,1),end:iso(year,month+1,0),previousStart:iso(year,month-1,1),previousEnd:iso(year,month,0)};
  if (period === "year") return {start:iso(year,0,1),end:iso(year,11,31),previousStart:iso(year-1,0,1),previousEnd:iso(year-1,11,31)};
  return {start:"1970-01-01",end:on,previousStart:"1970-01-01",previousEnd:"1970-01-01"};
}
export function practiceDuration(value: number): string {
  const n = Math.max(0, Math.round(value)), hours = Math.floor(n/3600), minutes = Math.floor(n%3600/60), seconds = n%60;
  return `${hours ? `${hours}h ` : ""}${hours ? String(minutes).padStart(2,"0") : minutes}m ${String(seconds).padStart(2,"0")}s`;
}
export interface PracticeTotals { totalSeconds: number; sessions: number; students: number; practiceDays: number; taskSeconds: number; tools: Record<PracticeTool, number> }
export interface PracticeCourse { studentId: string; pupilId: string | null; name: string; tutorId: string; tutorName: string; instrument: string; domain: "private" | "school" }
export interface PracticeMetricSession { id: string; studentId: string; studentName?: string; startedAt: string; durationSeconds: number; name: string; notes: string | null; instruments: string[]; tools: PracticeTool[]; tasks: string[]; partial: boolean; /** Present on updated servers; true only for complete manual/legacy sessions the family can correct. */ manualEditable?: boolean }
export interface PracticeMetricsSummary {
  version: 1; timezone: string; period: PracticePeriod; on: string; start: string; end: string; totals: PracticeTotals;
  days: { date: string; totalSeconds: number; tools: Record<PracticeTool,number> }[];
  comparison: { previousSeconds: number; differenceSeconds: number; through: string; comparable: boolean };
  record: { seconds: number; currentSeconds: number; week: string | null; isNew: boolean; secondsToBeat: number } | null;
  streak: { current: number; longest: number } | null;
  courses: PracticeCourse[]; instruments: string[]; unclassifiedSeconds: number; staff: boolean; admin: boolean; schoolAdmin: boolean;
  /** Additive: older servers omit the per-instrument breakdown. */
  instrumentTotals?: { instrument: string | null; totalSeconds: number }[];
}
/**
 * Personal insights, derived only from a `PracticeMetricsSummary` the Hub has
 * already built from `practice_metric_parts`. Nothing here re-measures
 * practice: overlapping tool intervals, manual corrections and deletions have
 * been settled by the server, so every figure reconciles with the summary
 * total, and therefore with the Practice History list for the same learner,
 * period and instrument filter. Nothing compares one learner with another.
 */
export interface PracticeWeekConsistency { weekStart: string; daysPracticed: number; totalSeconds: number }
/** Days practised in each London Monday-to-Sunday week that touches the summary's range. */
export function weeklyConsistency(summary: Pick<PracticeMetricsSummary, "days" | "start" | "end">): PracticeWeekConsistency[] {
  const weeks = new Map<string, PracticeWeekConsistency>();
  for (let week = practiceWeek(summary.start); week <= summary.end; week = shiftPracticeDay(week, 7)) {
    weeks.set(week, { weekStart: week, daysPracticed: 0, totalSeconds: 0 });
  }
  for (const day of summary.days) {
    if (day.totalSeconds <= 0) continue;
    const week = weeks.get(practiceWeek(day.date));
    if (!week) continue;
    week.daysPracticed += 1;
    week.totalSeconds += day.totalSeconds;
  }
  return [...weeks.values()];
}
export interface PracticeOwnComparison { currentSeconds: number; previousSeconds: number; differenceSeconds: number; through: string; comparable: boolean }
/** This period against the same learner's previous one, to the same point if the period is still running. */
export function ownPeriodComparison(summary: Pick<PracticeMetricsSummary, "totals" | "comparison">): PracticeOwnComparison {
  return {
    currentSeconds: summary.totals.totalSeconds,
    previousSeconds: summary.comparison.previousSeconds,
    differenceSeconds: summary.comparison.differenceSeconds,
    through: summary.comparison.through,
    comparable: summary.comparison.comparable,
  };
}
export interface PracticeInstrumentDuration { instrument: string | null; totalSeconds: number }
/** Exact-second time per instrument, longest first; unclassified practice keeps its own row. */
export function instrumentDurations(summary: Pick<PracticeMetricsSummary, "instrumentTotals">): PracticeInstrumentDuration[] {
  return [...(summary.instrumentTotals ?? [])]
    .filter(entry => entry.totalSeconds > 0)
    .sort((a, b) => b.totalSeconds - a.totalSeconds || String(a.instrument).localeCompare(String(b.instrument)));
}
export interface PracticeLeaderboardEntry { rank: number; initials: string; instruments: string[]; totalSeconds: number; sessions: number; self: boolean; id?: string; name?: string }
export interface PracticeLeaderboard { entries: PracticeLeaderboardEntry[]; own: PracticeLeaderboardEntry | null; students: number; page: number; pages: number; group: "student" | "tutor" | "instrument" }
